import { useId, useState } from 'react';
import {
  ArrowRight,
  ArrowUpRight,
  Check,
  ChevronDown,
  Clock3,
  RotateCcw,
  ShieldCheck,
  X,
} from 'lucide-react';

import { Container } from '@/components/ui/container';
import { Eyebrow, Section } from '@/components/ui/section';
import { EthIcon, UsdcIcon } from '@/components/ui/token-icons';
import { cn } from '@/lib/utils';
import {
  DEFAULT_CONTRACT_POLICY,
  DEFAULT_TRANSFER_POLICY,
  DEMO_DESTINATIONS,
  DEMO_MEMBER_COUNT,
  DEMO_REQUESTS,
  demoContractName,
  demoRequestAmount,
  demoRequestArguments,
  evaluateDemoRequest,
  formatDemoAmount,
  parseDemoAmount,
  type DemoContractPolicy,
  type DemoContractFunction,
  type DemoPolicy,
  type DemoRequest,
  type DemoTransactionType,
  type DemoTransferPolicy,
} from './policy-playground-model';
import './policy-page.css';

function AssetSelector({
  name,
  value,
  onChange,
}: {
  name: string;
  value: DemoTransferPolicy['token'];
  onChange: (asset: DemoTransferPolicy['token']) => void;
}) {
  return (
    <div className="policy-token-options">
      {(['any', 'ETH', 'USDC'] as const).map((asset) => (
        <label key={asset} className={cn(value === asset && 'is-selected')}>
          <input
            type="radio"
            name={name}
            value={asset}
            checked={value === asset}
            onChange={() => onChange(asset)}
          />
          {asset === 'ETH' ? (
            <EthIcon width={19} height={19} aria-hidden="true" />
          ) : asset === 'USDC' ? (
            <UsdcIcon width={19} height={19} aria-hidden="true" />
          ) : (
            <span className="policy-any-token" aria-hidden="true">
              ∗
            </span>
          )}
          {asset === 'any' ? 'Any' : asset}
        </label>
      ))}
    </div>
  );
}

function NumberInput({
  id,
  value,
  invalid,
  unit,
  prefix,
  disabled = false,
  onChange,
}: {
  id: string;
  value: string;
  invalid: boolean;
  unit?: string;
  prefix?: string;
  disabled?: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <div className={cn('policy-amount-input', invalid && 'is-invalid')}>
      {prefix && <span aria-hidden="true">{prefix}</span>}
      <input
        id={id}
        type="number"
        min="0"
        step="any"
        value={value}
        disabled={disabled}
        placeholder={disabled ? 'Any amount' : undefined}
        onChange={(event) => onChange(event.target.value)}
        aria-invalid={invalid}
      />
      {unit && <span>{unit}</span>}
    </div>
  );
}

function SampleRequest({
  request,
  policy,
}: {
  request: DemoRequest;
  policy: DemoPolicy;
}) {
  const result = evaluateDemoRequest(policy, request);
  const StatusIcon = { ready: Check, awaiting: Clock3, blocked: X }[
    result.status
  ];
  const { token, amount } = demoRequestAmount(request);
  const TokenIcon =
    token === 'USDC' ? UsdcIcon : token === 'ETH' ? EthIcon : null;
  return (
    <article
      aria-label={request.title}
      data-request-id={request.id}
      data-status={result.status}
      className={cn('policy-request', `is-${result.status}`)}
    >
      <div className="policy-request-top">
        <div className="policy-request-title">
          <ArrowUpRight aria-hidden="true" />
          <div>
            <h3>
              {request.type === 'token-transfer' ? (
                <>
                  Send {formatDemoAmount(amount)}{' '}
                  {TokenIcon && (
                    <TokenIcon width={18} height={18} aria-hidden="true" />
                  )}
                  {token}
                </>
              ) : (
                <>
                  <span>Contract call</span>
                  <span className="policy-request-contract">
                    <span>{demoContractName(request.contract)}</span>
                    <ArrowRight aria-hidden="true" />
                    <span>{request.function}</span>
                  </span>
                </>
              )}
            </h3>
          </div>
        </div>
        <span className="policy-request-status">
          <StatusIcon aria-hidden="true" />
          {result.label}
        </span>
      </div>
      {request.type === 'token-transfer' && (
        <p className="policy-request-action">To {request.recipient}</p>
      )}
      {request.type === 'contract-interaction' && (
        <dl className="policy-request-arguments">
          {demoRequestArguments(request).map(({ name, value }) => (
            <div key={name}>
              <dt>
                <code>{name}</code>
              </dt>
              <dd>
                {name === 'asset' && TokenIcon && (
                  <TokenIcon width={14} height={14} aria-hidden="true" />
                )}
                <span>{value}</span>
              </dd>
            </div>
          ))}
        </dl>
      )}
      <div className="policy-request-signatures">
        <div aria-hidden="true">
          {Array.from({ length: DEMO_MEMBER_COUNT }, (_, index) => (
            <span
              key={index}
              className={cn(index < request.approvals && 'is-signed')}
            >
              {index < request.approvals ? <Check /> : null}
            </span>
          ))}
        </div>
        <span>
          {request.approvals} of {DEMO_MEMBER_COUNT} approved
        </span>
      </div>
      <ul className="policy-request-reasons">
        {result.reasons.map((reason) => (
          <li key={reason}>{reason}</li>
        ))}
      </ul>
    </article>
  );
}

export function PolicyPlayground() {
  const controlId = useId();
  const [transactionType, setTransactionType] =
    useState<DemoTransactionType>('token-transfer');
  // Keep both policies independently: changing an argument cannot alter transfer rules.
  const [transfer, setTransfer] = useState<DemoTransferPolicy>({
    ...DEFAULT_TRANSFER_POLICY,
  });
  const [contract, setContract] = useState<DemoContractPolicy>({
    ...DEFAULT_CONTRACT_POLICY,
  });
  const [transferAmount, setTransferAmount] = useState(
    String(DEFAULT_TRANSFER_POLICY.maxAmount)
  );
  const [contractAmount, setContractAmount] = useState(
    String(DEFAULT_CONTRACT_POLICY.amount)
  );
  const policy = transactionType === 'token-transfer' ? transfer : contract;

  function resetPolicy() {
    if (transactionType === 'token-transfer') {
      setTransfer({ ...DEFAULT_TRANSFER_POLICY });
      setTransferAmount(String(DEFAULT_TRANSFER_POLICY.maxAmount));
    } else {
      setContract({ ...DEFAULT_CONTRACT_POLICY });
      setContractAmount(String(DEFAULT_CONTRACT_POLICY.amount));
    }
  }

  function updateApprovals(value: string) {
    const requiredApprovals = Number(value);
    if (transactionType === 'token-transfer')
      setTransfer((current) => ({ ...current, requiredApprovals }));
    else setContract((current) => ({ ...current, requiredApprovals }));
  }

  function toggleDestination(destination: string) {
    setTransfer((current) => ({
      ...current,
      destinations: current.destinations.includes(destination)
        ? current.destinations.filter((value) => value !== destination)
        : [...current.destinations, destination],
    }));
  }

  return (
    <Section
      id="policy-playground"
      className="policy-playground border-t border-hairline bg-surface py-8 sm:py-8 lg:py-8"
    >
      <Container>
        <div className="policy-playground-intro">
          <Eyebrow>Try a policy</Eyebrow>
          <h2>
            Change the rules.
            <br />
            <span>See what gets through.</span>
          </h2>
        </div>
        <div className="policy-playground-layout">
          <div className="policy-controls" id="policy-controls">
            <div className="policy-controls-heading">
              <span>
                <ShieldCheck aria-hidden="true" /> Your policy
              </span>
              <button
                type="button"
                onClick={resetPolicy}
                aria-label="Reset policy"
              >
                <RotateCcw aria-hidden="true" /> Reset
              </button>
            </div>
            <div className="policy-control">
              <label htmlFor={`${controlId}-type`}>Transaction type</label>
              <div className="policy-select">
                <select
                  id={`${controlId}-type`}
                  value={transactionType}
                  onChange={(event) =>
                    setTransactionType(
                      event.target.value as DemoTransactionType
                    )
                  }
                >
                  <option value="token-transfer">Token transfer</option>
                  <option value="contract-interaction">
                    Contract interaction
                  </option>
                </select>
                <ChevronDown aria-hidden="true" />
              </div>
            </div>

            {/* Both forms share a grid cell to reserve the taller form's height. The inactive form stays inert and hidden from assistive technology. */}
            <div className="policy-type-panels">
              <div
                className="policy-type-panel"
                aria-hidden={transactionType !== 'token-transfer'}
                inert={transactionType !== 'token-transfer'}
              >
                <fieldset className="policy-control">
                  <legend>Token</legend>
                  <AssetSelector
                    name={`${controlId}-transfer-token`}
                    value={transfer.token}
                    onChange={(token) =>
                      setTransfer((current) => ({ ...current, token }))
                    }
                  />
                </fieldset>
                <div className="policy-control">
                  <label htmlFor={`${controlId}-transfer-amount`}>
                    Maximum token amount
                  </label>
                  <NumberInput
                    id={`${controlId}-transfer-amount`}
                    value={transfer.token === 'any' ? '' : transferAmount}
                    disabled={transfer.token === 'any'}
                    invalid={
                      transfer.token !== 'any' && transfer.maxAmount === null
                    }
                    prefix={transfer.token === 'any' ? '' : 'At most'}
                    unit={transfer.token === 'any' ? '' : transfer.token}
                    onChange={(value) => {
                      setTransferAmount(value);
                      setTransfer((current) => ({
                        ...current,
                        maxAmount: parseDemoAmount(value),
                      }));
                    }}
                  />
                </div>
                <fieldset className="policy-control">
                  <legend>Destinations</legend>
                  <div className="policy-destination-options">
                    {DEMO_DESTINATIONS.map((destination, index) => (
                      <label
                        key={destination}
                        htmlFor={`${controlId}-destination-${index}`}
                      >
                        <input
                          id={`${controlId}-destination-${index}`}
                          type="checkbox"
                          checked={transfer.destinations.includes(destination)}
                          onChange={() => toggleDestination(destination)}
                        />
                        <span>{destination}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>
              </div>

              <div
                className="policy-type-panel"
                aria-hidden={transactionType !== 'contract-interaction'}
                inert={transactionType !== 'contract-interaction'}
              >
                <div className="policy-control policy-contract-scope">
                  <dl>
                    <div>
                      <dt>Contract</dt>
                      <dd>Lending pool</dd>
                    </div>
                  </dl>
                </div>
                <div className="policy-control">
                  <label htmlFor={`${controlId}-function`}>Function</label>
                  <div className="policy-select">
                    <select
                      id={`${controlId}-function`}
                      value={contract.function}
                      onChange={(event) =>
                        setContract((current) => ({
                          ...current,
                          function: event.target.value as DemoContractFunction,
                        }))
                      }
                    >
                      <option value="supply">supply(asset, amount)</option>
                      <option value="withdraw">withdraw(asset, amount)</option>
                    </select>
                    <ChevronDown aria-hidden="true" />
                  </div>
                </div>
                <fieldset className="policy-control">
                  <legend>
                    <code>asset</code>
                  </legend>
                  <AssetSelector
                    name={`${controlId}-contract-asset`}
                    value={contract.asset}
                    onChange={(asset) =>
                      setContract((current) => ({ ...current, asset }))
                    }
                  />
                </fieldset>
                <fieldset className="policy-control">
                  <legend>
                    <code>amount</code>
                  </legend>
                  <div className="policy-parameter-value">
                    <label
                      className="sr-only"
                      htmlFor={`${controlId}-contract-amount`}
                    >
                      amount value
                    </label>
                    <NumberInput
                      id={`${controlId}-contract-amount`}
                      value={contractAmount}
                      invalid={contract.amount === null}
                      prefix="At most"
                      unit={contract.asset === 'any' ? 'units' : contract.asset}
                      onChange={(value) => {
                        setContractAmount(value);
                        setContract((current) => ({
                          ...current,
                          amount: parseDemoAmount(value),
                        }));
                      }}
                    />
                  </div>
                </fieldset>
              </div>
            </div>
            <div className="policy-control">
              <label htmlFor={`${controlId}-approvals`}>
                Required approvals
              </label>
              <div className="policy-select">
                <select
                  id={`${controlId}-approvals`}
                  value={policy.requiredApprovals}
                  onChange={(event) => updateApprovals(event.target.value)}
                >
                  {Array.from({ length: DEMO_MEMBER_COUNT + 1 }, (_, count) => (
                    <option value={count} key={count}>
                      {count} of {DEMO_MEMBER_COUNT}
                      {count === 0 ? ' · automatic' : ' members'}
                    </option>
                  ))}
                </select>
                <ChevronDown aria-hidden="true" />
              </div>
            </div>
          </div>
          <div
            className="policy-request-grid"
            role="group"
            aria-label="Policy transaction examples"
          >
            {DEMO_REQUESTS.map((request) => (
              <SampleRequest
                key={request.id}
                request={request}
                policy={policy}
              />
            ))}
          </div>
        </div>
      </Container>
    </Section>
  );
}
